import { useCallback, useState } from "react";

import { useTwoFactorAction, type UseTwoFactorActionResult } from "@/shared/hooks/useTwoFactorAction";

import { PayoutBatchesService } from "../services/payoutBatches.service";
import { type PayoutBatchDetail, PayoutBatchStatus } from "../types/payoutBatches.types";
import { type BatchError, classifyBatchError } from "../utils/batchErrors";

/**
 * `form` → the person reads and confirms; `sending` → the code was accepted and the POST is in flight;
 * `conflict` → the server refused (the batch changed, is busy, expired…) and the only exit is to review
 * again; `unconfirmed` → the request may have landed but the answer was lost, so the batch is re-read; `done`
 * → the batch is APPROVED/EXECUTING.
 */
export type ExecuteStage = "form" | "sending" | "conflict" | "unconfirmed" | "done";

export interface ExecuteBatchInput {
  fingerprint: string;
  acknowledgeProviderBalance: boolean;
}

export interface UseExecuteBatchResult {
  stage: ExecuteStage;
  error: BatchError | null;
  /** Resolves with the stage the attempt ended in, so the dialog can close on `done` without waiting a render. */
  execute: (input: ExecuteBatchInput) => Promise<ExecuteStage>;
  reset: () => void;
  twoFactor: UseTwoFactorActionResult<PayoutBatchDetail>;
}

/**
 * `POST /:id/execute` behind the step-up prompt. A 409 is never retried: the fingerprint the person approved
 * no longer describes the batch, so the dialog sends them back to review. A lost answer is resolved by
 * re-reading — the run may well have started.
 */
export function useExecuteBatch(
  batchId: string,
  applyBatch: (next: PayoutBatchDetail) => void,
  refresh: () => Promise<PayoutBatchDetail | null>,
): UseExecuteBatchResult {
  const twoFactor = useTwoFactorAction<PayoutBatchDetail>();
  const [stage, setStage] = useState<ExecuteStage>("form");
  const [error, setError] = useState<BatchError | null>(null);
  const { start } = twoFactor;

  const execute = useCallback(
    async (input: ExecuteBatchInput): Promise<ExecuteStage> => {
      setError(null);
      const settle = (next: ExecuteStage): ExecuteStage => {
        setStage(next);
        return next;
      };
      try {
        const result = await start(async (code) => {
          setStage("sending");
          const { data } = await PayoutBatchesService.execute(
            batchId,
            {
              fingerprint: input.fingerprint,
              acknowledgeProviderBalance: input.acknowledgeProviderBalance,
            },
            code,
          );
          return data;
        });
        if (!result) return settle("form");
        applyBatch(result);
        return settle("done");
      } catch (err: unknown) {
        const classified = classifyBatchError(err);
        setError(classified);
        if (classified.kind === "network") {
          settle("unconfirmed");
          const status = (await refresh())?.status;
          return status === PayoutBatchStatus.APPROVED || status === PayoutBatchStatus.EXECUTING
            ? settle("done")
            : "unconfirmed";
        }
        if (
          classified.kind === "conflict" ||
          classified.kind === "bad_request" ||
          classified.kind === "verification_required"
        ) {
          return settle("conflict");
        }
        return settle("form");
      }
    },
    [applyBatch, batchId, refresh, start],
  );

  const reset = useCallback(() => {
    setStage("form");
    setError(null);
  }, []);

  return { stage, error, execute, reset, twoFactor };
}
